import React from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { locationApi } from '../LocationApi';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { MESSAGE_LOCATION_EXPORT_SUCCESS, MESSAGE_LOCATION_EXPORT_ERROR } from 'config/messages';
import { createDownloadLink } from 'lib/helpers';
import { ILocationExportOperationArgs } from '@pepperhq/types-sdk';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { ILocationExportOperation } from '../models/LocationOperationModel';
import logger from 'lib/logger';

interface UseLocationExportReturn {
    exportIsInProgress: boolean;
    exportDialogOpen: boolean;
    onExport: () => void;
}

const DEFAULT_POLL_INTERVAL = 3000;

export function useLocationExport(): UseLocationExportReturn {
    const dispatch = useDispatch();
    const [currentOperation, setCurrentOperation] = React.useState<ILocationExportOperation>(undefined);
    const [isCreateOperationLoading, setIsCreateOperationLoading] = React.useState(false);
    const [exportDialogOpen, setExportDialogOpen] = React.useState(false);

    const timeoutRef = React.useRef<NodeJS.Timeout | undefined>();
    const closeTimeoutRef = React.useRef<NodeJS.Timeout | undefined>();

    const isInProgress = React.useMemo(() => {
        if (isCreateOperationLoading) {
            return true;
        }
        return !!currentOperation;
    }, [currentOperation, isCreateOperationLoading]);

    // Initialize: fetch pending operations on mount
    React.useEffect(() => {
        locationApi
            .getExportOperations()
            .then(res => {
                if (!res.ok || !res.body) {
                    throw new Error(res.body?.message || 'Failed to fetch the location export operations.');
                }

                const operation = res.body.items.find(
                    operation =>
                        operation.status === OperationStatus.PENDING ||
                        operation.status === OperationStatus.RUNNING
                );

                if (operation) {
                    setCurrentOperation(operation);
                }
            })
            .catch(e => {
                logger.error('Failed to load pending exports', e);
            });
    }, []);

    // Clear all timeouts on unmount
    React.useEffect(
        () => () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        []
    );

    const clearOperation = React.useCallback(() => {
        setCurrentOperation(undefined);
    }, []);

    const handleOperationSuccess = React.useCallback(
        async (href: string, createdAt: string) => {
            const downloadResponse = await locationApi.downloadExportFile(href);
            if (!downloadResponse.ok) {
                throw new Error(downloadResponse.statusText);
            }
            const blob = await downloadResponse.blob();
            const fileName = `locations-export-${new Date(createdAt).toISOString()}.csv`;
            createDownloadLink(blob, fileName);
            dispatch(
                enqueueSnackbar(MESSAGE_LOCATION_EXPORT_SUCCESS, {
                    variant: 'success'
                })
            );
            closeTimeoutRef.current = setTimeout(() => {
                setExportDialogOpen(false);
            }, 2000);
            clearOperation();
        },
        [clearOperation, dispatch]
    );

    const handleOperationError = React.useCallback(() => {
        setExportDialogOpen(false);
        clearOperation();
    }, [clearOperation]);

    const pollOperation = React.useCallback<undefined | (() => void)>(() => {
        if (currentOperation?._id) {
            timeoutRef.current = setTimeout(async () => {
                try {
                    const res = await locationApi.getExportOperation(currentOperation._id);
                    if (!res.ok) {
                        throw new Error('Failed to fetch operation status');
                    }

                    const operation = res.body;
                    switch (operation.status) {
                        case OperationStatus.PENDING:
                        case OperationStatus.RUNNING:
                            return pollOperation();
                        case OperationStatus.DONE:
                            if (operation.result?.href) {
                                handleOperationSuccess(operation.result.href, operation.createdAt);
                            }
                            break;
                        case OperationStatus.ERROR:
                        case OperationStatus.TIMEOUT:
                            throw new Error(
                                operation.error && operation.error.length > 0
                                    ? operation.error[0]
                                    : MESSAGE_LOCATION_EXPORT_ERROR
                            );
                    }
                } catch (e) {
                    logger.error(e.message);
                    dispatch(enqueueSnackbar(MESSAGE_LOCATION_EXPORT_ERROR, { variant: 'error' }));
                    handleOperationError();
                }
            }, DEFAULT_POLL_INTERVAL);
        }
    }, [currentOperation, dispatch, handleOperationSuccess, handleOperationError]);

    React.useEffect(() => {
        if (currentOperation) {
            return pollOperation();
        }
    }, [currentOperation, pollOperation]);

    const startExport = React.useCallback(
        async (filters?: ILocationExportOperationArgs) => {
            setIsCreateOperationLoading(true);
            const nonce = uuid();

            try {
                const exportRes = await locationApi.exportLocations(filters, nonce);
                if (!exportRes.ok) {
                    throw new Error(MESSAGE_LOCATION_EXPORT_ERROR);
                }
                setCurrentOperation(exportRes.body);
            } catch (error) {
                dispatch(enqueueSnackbar(MESSAGE_LOCATION_EXPORT_ERROR, { variant: 'error' }));
            } finally {
                setIsCreateOperationLoading(false);
            }
        },
        [dispatch]
    );

    const handleExportClick = React.useCallback(async () => {
        setExportDialogOpen(true);
        await startExport();
    }, [startExport]);

    return {
        exportIsInProgress: isInProgress,
        exportDialogOpen,
        onExport: handleExportClick
    };
}
